import { observer } from 'mobx-react-lite'
import { useState, type ReactElement } from 'react'
import { useSearchParams } from 'react-router-dom'
import { AiAgentCallLogTab } from 'app/components/ai-agent-call-log/AiAgentCallLogTab'
import { AiAgentVoiceComponent } from 'app/components/ai-agent-voice/AiAgentVoiceComponent'
import { CALL_LOG_TAB, defaultFiltersEncoded, useAiAgentVoice } from 'app/components/ai-agent-voice/useAiAgentVoice'
import { VoiceAgentOpsTab } from 'app/components/ai-agent-voice-ops/VoiceAgentOpsTab'
import DashboardHeader from 'app/components/analytics/DashboardHeader'
import { useUser } from 'app/utils'
import { AgentLiveStatus } from './AgentLiveStatus'
import { AgentSubTabs, AgentsShell } from './AgentsShell'

const DASHBOARD_TAB = `Dashboard`
const OPS_TAB = `Ops`
const SUPER_USER_TABS = [OPS_TAB]

/** Agents → Voice once the agent runs: the Analytics → Voice Agent dashboard, under the Agents tabs. */
export const AgentsVoiceDashboard = observer(function AgentsVoiceDashboard(): ReactElement {
  const [searchParams, setSearchParams] = useSearchParams()
  const props = useAiAgentVoice()
  const tabOptions =
    useUser()?.type === `super` ? [DASHBOARD_TAB, CALL_LOG_TAB, ...SUPER_USER_TABS] : [DASHBOARD_TAB, CALL_LOG_TAB]
  const [selectedTab, setSelectedTab] = useState(searchParams.get(`tab`) ?? DASHBOARD_TAB)
  const openTab = tabOptions.includes(selectedTab) ? selectedTab : DASHBOARD_TAB

  const handleTabChange = (tab: string): void => {
    setSelectedTab(tab)
    setSearchParams(
      (prev) => {
        prev.set(`tab`, tab)
        if (tab === DASHBOARD_TAB) prev.set(`filters`, defaultFiltersEncoded)
        else prev.delete(`filters`)
        return prev
      },
      { replace: true },
    )
  }

  return (
    <AgentsShell
      header={
        <DashboardHeader
          title="Agents"
          noticeClassName="mb-6"
          dateChangeHandler={props.dateChangeHandler}
          hideCompare={true}
          hideChannelSelector={true}
          hideLocationSelector={props.locationCount <= 1}
          locationProduct="AI Agent"
          requireProductInUse
          hideUniqueNewPatientsToggle={true}
          defaultDateRange="this_month"
          integrationWarningSpacing
        />
      }
    >
      <AgentSubTabs
        value={openTab}
        onChange={handleTabChange}
        tabs={tabOptions}
        superUserTabs={SUPER_USER_TABS}
        trailing={<AgentLiveStatus kind="voice" />}
      />
      {openTab === OPS_TAB ? (
        <VoiceAgentOpsTab />
      ) : openTab === CALL_LOG_TAB ? (
        <AiAgentCallLogTab />
      ) : (
        <AiAgentVoiceComponent {...props} />
      )}
    </AgentsShell>
  )
})
